import React, { useEffect, useState, useRef } from 'react';
import { usePropsContext } from '@uhg-abyss/web/hooks/usePropsContext';
import { Button } from '@uhg-abyss/web/ui/Button';
import { styled } from '@uhg-abyss/web/tools/styled';
import { Drawer } from '@uhg-abyss/web/ui/Drawer';
import { Accordion } from '@uhg-abyss/web/ui/Accordion';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { useToast } from '@uhg-abyss/web/hooks/useToast';
import { usePPDLagoon } from '@sde/widgets-core/src/tools/lagoon';
import { ProviderSelectDrawerErrorModal } from '../../common/ProviderSelectDrawerErrorModal';
import { Corporates } from './Corporates';
import { CareProvider } from './CareProvider';
import { TaxIdNumbers } from './TaxIdNumbers';
import { ProviderConfirmationModal } from './ProviderConfirmationModal';
import { usercache } from '../../../../redux/tools/usercache';
import { useSaveProviderPreferenceQuery } from '../../../../common/hooks/useSaveProviderPreferenceQuery';
import { useOrganizationsQuery } from '../../../../common/hooks/useOrganizationsQuery';
import { useProvidersQuery } from '../../../../common/hooks/useProvidersQuery';
import { toastHandler } from '../../../../common/constants/toasts/toastHandler';

const StyledProviderDrawer = styled(Drawer, {
  '&.abyss-modal-overlay': {
    zIndex: 999999,
  },
  '&.abyss-modal-content-container': {
    backgroundColor: '$gray1',
  },
  '&.abyss-modal-footer': {
    borderTop: '1px solid $gray4',
    backgroundColor: '$white',
  },
});

const StyledAccordionItem = styled(Accordion.Item, {
  borderRadius: '8px !important',
  border: '1px solid $gray4 !important',
  marginBottom: '8px',
  '.abyss-accordion-content-text': {
    paddingTop: '0px !important',
    paddingLeft: '0px !important',
    paddingRight: '0px !important',
  },

  '.abyss-accordion-header': {
    fontWeight: 'bold',
    color: '$primary1 !important',
  },
  '.abyss-accordion-header-container': {
    paddingTop: '10px',
    paddingBottom: '18px',
  },
  '.abyss-accordion-header-subtext': {
    fontSize: '16px',
    marginTop: '4px',
  },
  dynamic: ({ isDisabled, allowProviderSearch }) => {
    if (isDisabled) {
      return {
        '.abyss-accordion-header': {
          color: allowProviderSearch
            ? '$gray5 !important'
            : '$primary1 !important',
        },
        '.abyss-accordion-header-subtext': {
          color: 'black !important',
        },
      };
    }
    return {};
  },
});

export const ProviderSelectDrawer = () => {
  const { data, events } = usePropsContext();
  const { getMessage } = usePPDLagoon();
  const {
    isProviderOpen,
    user,
    provider,
    confirmSelection,
    toggleProvider,
  } = data;
  const {
    uuid,
    isInternalUser,
    isIntakeUser,
    isPSAUser,
    isPdoAdmin,
    isPaanAdmin,
    isPaceAdmin,
    isObpUser,
    corporates,
  } = user;
  const { toast } = useToast();
  const isInitialCareProvidersLoad = useRef(true);
  const isInitialOrganizationsLoad = useRef(true);
  const [organizations, setOrganizations] = useState([]);
  const [isOrganizationsLoading, setIsOrganizationsLoading] = useState(false);
  const [initialOrganizationsData, setInitialOrganizationsData] = useState([]);
  const [careProviders, setCareProviders] = useState([]);
  const [isCareProvidersLoading, setIsCareProvidersLoading] = useState(false);
  const [initialCareProvidersData, setInitialCareProvidersData] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState({});
  const [defaultProvider, setDefaultProvider] = useState({});
  const [confirmProvider, setConfirmProvider] = useState(false);
  const [canCallProviders, setCanCallProviders] = useState(false);
  const [expandedValue, setExpandedValue] = useState('');
  const [isOwnerRowClicked, setIsOwnerRowClicked] = useState(false);
  const [isTaxIdRowClicked, setIsTaxIdRowClicked] = useState(false);
  const [isAccordionDisabled, setIsAccordionDisabled] = useState(false);
  const [isSaveDisabled, setIsSaveDisabled] = useState(false);
  const [
    isCareProviderAccordionDisabled,
    setIsCareProviderAccordionDisabled,
  ] = useState(false);
  const [isTaxIdAccordionDisabled, setIsTaxIdAccordionDisabled] = useState(
    false
  );
  const [errorCode, setErrorCode] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [organizationsError, setOrganizationsError] = useState(false);
  const [careProvidersError, setCareProvidersError] = useState(false);
  const [allowProviderSearch, setAllowProviderSearch] = useState(
    !(
      isIntakeUser ||
      isInternalUser ||
      isPSAUser ||
      isPaanAdmin ||
      isPaceAdmin ||
      isPdoAdmin ||
      isObpUser
    )
  );
  const [isOwnerAccordionExpanded, setIsOwnerAccordionExpanded] = useState(
    false
  );
  const [isTaxIdAccordionExpanded, setIsTaxIdAccordionExpanded] = useState(
    false
  );
  const [
    isCareProviderAccordionExpanded,
    setIsCareProviderAccordionExpanded,
  ] = useState(false);
  const payerAndProviderSelectionSuccessToastMessage = getMessage(
    'payer-and-provider-selection-success-toast-message'
  );
  const providerSelectionErrorToastMessage = getMessage(
    'provider-selection-error-toast-message'
  );

  useEffect(() => {
    if (user && user.isUserApiError) {
      setErrorCode(user.code);
      setErrorMessage(getMessage(`validate-user[${user.code}]`));
    }
  }, [user]);

  useEffect(() => {
    if (allowProviderSearch) {
      setIsOwnerAccordionExpanded(true);
      setExpandedValue('corporate-tax-id-owner');
    } else {
      setIsAccordionDisabled(true);
    }
  }, [isProviderOpen]);

  const [
    organizationsQueryResult,
    getOrganizationsQuery,
  ] = useOrganizationsQuery({
    onCompleted: (response) => {
      setOrganizationsError(false);
      setIsTaxIdAccordionDisabled(false);
      if (
        response.organizations &&
        response.organizations?.permissions?.length > 0
      ) {
        if (isInitialOrganizationsLoad.current) {
          setInitialOrganizationsData(response.organizations.permissions);
          isInitialOrganizationsLoad.current = false;
        }
        setOrganizations(response.organizations.permissions);
      } else {
        setOrganizations([]);
      }
      setIsOrganizationsLoading(false);
    },
    onError: (err) => {
      setIsOrganizationsLoading(false);
      setOrganizations([]);
      setOrganizationsError(true);
      setCareProvidersError(true);
      const { code, message } = err;
      events.onDigitalSecurityError({
        code,
        message,
      });
    },
  });

  const [careProvidersQueryResult, getCareProvidersQuery] = useProvidersQuery({
    onCompleted: (response) => {
      setCanCallProviders(false);
      setCareProvidersError(false);
      setIsCareProviderAccordionDisabled(false);
      if (response.providers && response.providers?.length > 0) {
        if (isInitialCareProvidersLoad.current) {
          setInitialCareProvidersData(response.providers);
          isInitialCareProvidersLoad.current = false;
        }
        setCareProviders(response.providers);
      }
      setIsCareProvidersLoading(false);
    },
    onError: (err) => {
      setIsCareProvidersLoading(false);
      setCareProvidersError(true);
      setCanCallProviders(false);
      events.onError(err);
      const { code, message } = err;
      setCareProviders([]);
      events.onDigitalSecurityError({
        code,
        message,
      });
    },
  });

  const [
    saveProviderPreferenceResult,
    saveProviderPreference,
  ] = useSaveProviderPreferenceQuery({
    onCompleted: (response) => {
      const { provider: providerResponse } = response;
      const foundOrg = organizations.find(
        (org) => org.tin === providerResponse.taxIdNumber
      );
      const responseFormatted = {
        ...response,
        provider: {
          ...providerResponse,
          permissionCode: foundOrg.permissionCode || [],
          permissionSubCode: foundOrg.permissionSubCode || [],
          privileges: foundOrg.privileges || [],
          allPermission: foundOrg.allPermission || false,
          tpsm: foundOrg.tpsm,
        },
        permissionCode: foundOrg.permissionCode || [],
        permissionSubCode: foundOrg.permissionSubCode || [],
        privileges: foundOrg.privileges || [],
        allPermission: foundOrg.allPermission || false,
      };

      if (!data.saveDisabled) {
        usercache.set('provider', provider);
      }
      events.onChange(responseFormatted);
      events.onSave(providerResponse.servicingProviderMpin);
      setConfirmProvider(false);
      setDefaultProvider(providerResponse);
      setIsSaveDisabled(false);
      isInitialOrganizationsLoad.current = true;
      isInitialCareProvidersLoad.current = true;
      events.onDrawerClose();
      toast.show(
        toastHandler(
          'Provider Selection Updated',
          payerAndProviderSelectionSuccessToastMessage,
          'success'
        )
      );
    },
    onError: (err) => {
      setIsSaveDisabled(false);
      setConfirmProvider(false);
      events.onError(err);
      toast.show(
        toastHandler(
          'Unable to save provider selection',
          providerSelectionErrorToastMessage,
          'error'
        )
      );
    },
  });

  const fetchOrganizations = (providerObj) => {
    const {
      corporateProviderMpin,
      uhcProviderId,
      userId,
      uniqueId,
    } = providerObj;

    getOrganizationsQuery({
      variables: {
        uuid,
        corporateProviderMpin,
        uhcProviderId,
        userId,
        uniqueId,
      },
    });
  };

  const fetchCareProviders = (providerObj) => {
    const {
      corporateProviderMpin,
      taxIdNumber,
      uhcProviderId,
      uniqueId,
      userId,
    } = providerObj;

    getCareProvidersQuery({
      variables: {
        corporateProviderMpin,
        taxIdNumber,
        uhcProviderId,
        uniqueId,
        userId,
        uuid,
      },
    });
  };

  useEffect(() => {
    if (provider) {
      setSelectedProvider(provider);
      setDefaultProvider(provider);
    }
  }, [provider]);

  useEffect(() => {
    if (
      isProviderOpen &&
      provider.corporateProviderMpin &&
      provider.userId &&
      provider.uhcProviderId
    ) {
      fetchOrganizations(provider);
    }
  }, [isProviderOpen, uuid, provider.uniqueId]);

  useEffect(() => {
    if (canCallProviders) {
      setIsCareProvidersLoading(true);
      fetchCareProviders(selectedProvider);
    }
  }, [canCallProviders, selectedProvider]);

  useEffect(() => {
    if (!data.saveDisabled) {
      const cachedProvider = usercache.get('provider');
      if (cachedProvider) {
        events.onChange({
          provider: {},
          permissions: [],
          organizations: [],
          ...cachedProvider,
        });
      }
    }
  }, [uuid, data.saveDisabled]);

  const handleSave = () => {
    saveProviderPreference({
      variables: {
        organization: {
          id: selectedProvider.organizationId,
          name: selectedProvider.organizationName,
          permissions:
            selectedProvider.privileges || selectedProvider.permissions,
          allPermission: selectedProvider.allPermission,
        },
        corporateProvider: {
          mpin: selectedProvider.corporateProviderMpin,
          name: selectedProvider.corporateProviderName,
          uniqueId: selectedProvider.uniqueId,
        },
        taxIdNumber: {
          tin: selectedProvider.taxIdNumber,
          tinType: selectedProvider.tinType,
        },
        servicingProvider: {
          mpin: selectedProvider.servicingProviderMpin,
          name: selectedProvider.servicingProviderName,
          firstName: selectedProvider?.servicingProviderFirstName,
          lastName: selectedProvider?.servicingProviderLastName,
        },
        uuid,
        uhcProviderId: selectedProvider.uhcProviderId,
        userId: selectedProvider.userId,
        save: !data.saveDisabled, // this is always true since data.saveDisabled is never defined
        isActive: selectedProvider?.isActive,
        isThirdParty: selectedProvider?.isThirdParty,
        tpsm: selectedProvider?.tpsm,
      },
    });
  };

  const handleSelection = () => {
    if (confirmSelection) {
      setConfirmProvider(true);
      events.onDrawerClose();
    } else {
      handleSave();
    }
  };

  const handleProviderReset = () => {
    if (defaultProvider?.organization || defaultProvider?.uniqueId) {
      setSelectedProvider(defaultProvider);
    } else {
      setSelectedProvider({});
    }
    setCareProviders(initialCareProvidersData);
    setOrganizations(initialOrganizationsData);
  };

  const handleDrawerClose = () => {
    handleProviderReset();
    setIsOwnerRowClicked(false);
    setIsTaxIdRowClicked(false);
    setCanCallProviders(false);
    setExpandedValue('');
    setIsAccordionDisabled(false);
    setIsCareProviderAccordionDisabled(false);
    setIsTaxIdAccordionDisabled(false);
    setOrganizationsError(false);
    setCareProvidersError(false);
    setIsSaveDisabled(false);
    setConfirmProvider(false);
    events.onDrawerClose();
    events.onCancel();
  };

  const handleClose = () => {
    handleDrawerClose();
  };

  const handleCancel = () => {
    handleDrawerClose();
  };

  const handleNewCorporateSelection = (newCorporateSelection) => {
    setIsOrganizationsLoading(true);
    setOrganizations([]);
    setCareProviders([]);
    fetchOrganizations(newCorporateSelection);
  };

  const onValueChange = (e) => {
    setExpandedValue(e);
  };

  useEffect(() => {
    if (!expandedValue) {
      setIsOwnerAccordionExpanded(false);
      setIsTaxIdAccordionExpanded(false);
      setIsCareProviderAccordionExpanded(false);
    }

    if (expandedValue === 'corporate-tax-id-owner') {
      setIsOwnerAccordionExpanded(true);
      setIsTaxIdAccordionExpanded(false);
      setIsCareProviderAccordionExpanded(false);
    }

    if (expandedValue === 'tax-id-number') {
      setIsTaxIdAccordionExpanded(true);
      setIsOwnerAccordionExpanded(false);
      setIsCareProviderAccordionExpanded(false);
    }

    if (expandedValue === 'care-provider') {
      setIsCareProviderAccordionExpanded(true);
      setIsOwnerAccordionExpanded(false);
      setIsTaxIdAccordionExpanded(false);
    }
  }, [expandedValue]);

  return (
    <React.Fragment>
      <StyledProviderDrawer
        title={!user.isUserApiError && 'Select a care provider'}
        isOpen={isProviderOpen}
        onClose={handleClose}
        size="650px"
        model="drawer"
        data-testid="provider-drawer-title"
        footer={
          !user.isUserApiError && (
            <Drawer.Section>
              <Layout.Group alignLayout="right">
                <Button
                  data-testid="provider-drawer-cancel-button"
                  variant="outline"
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
                <Button
                  data-testid="provider-drawer-save-button"
                  isDisabled={
                    !allowProviderSearch ||
                    organizationsError ||
                    careProvidersError ||
                    isSaveDisabled
                  }
                  onClick={() => handleSelection()}
                  isLoading={saveProviderPreferenceResult.loading}
                >
                  Save
                </Button>
              </Layout.Group>
            </Drawer.Section>
          )
        }
      >
        <Choose>
          <When condition={user && user.isUserApiError}>
            <ProviderSelectDrawerErrorModal
              errorCode={errorCode}
              errorMessage={errorMessage}
              toggleProvider={toggleProvider}
            />
          </When>
          <Otherwise>
            <Drawer.Section>
              <Accordion
                isCollapsible
                value={expandedValue}
                onValueChange={onValueChange}
                defaultValue="corporate-tax-id-owner"
                type="single"
                isDisabled={isAccordionDisabled}
              >
                <StyledAccordionItem
                  isDisabled={isAccordionDisabled}
                  allowProviderSearch={allowProviderSearch}
                  value="corporate-tax-id-owner"
                >
                  <Corporates
                    corporates={corporates}
                    isOwnerAccordionExpanded={isOwnerAccordionExpanded}
                    selectedProvider={selectedProvider}
                    setSelectedProvider={setSelectedProvider}
                    setAllowProviderSearch={setAllowProviderSearch}
                    handleNewCorporateSelection={handleNewCorporateSelection}
                    setExpandedValue={setExpandedValue}
                    isCorporatesLoading={
                      organizationsQueryResult.loading ||
                      careProvidersQueryResult.loading
                    }
                    setIsOwnerRowClicked={setIsOwnerRowClicked}
                    setIsSaveDisabled={setIsSaveDisabled}
                    setCanCallProviders={setCanCallProviders}
                    setIsCareProviderAccordionDisabled={
                      setIsCareProviderAccordionDisabled
                    }
                    setIsTaxIdAccordionDisabled={setIsTaxIdAccordionDisabled}
                    isAccordionDisabled={isAccordionDisabled}
                  />
                </StyledAccordionItem>

                <StyledAccordionItem
                  isDisabled={
                    (!organizationsError && isTaxIdAccordionDisabled) ||
                    isAccordionDisabled
                  }
                  allowProviderSearch={allowProviderSearch}
                  value="tax-id-number"
                >
                  <TaxIdNumbers
                    organizations={organizations}
                    isTaxIdAccordionExpanded={isTaxIdAccordionExpanded}
                    organizationsError={organizationsError}
                    selectedProvider={selectedProvider}
                    setSelectedProvider={setSelectedProvider}
                    isOwnerRowClicked={isOwnerRowClicked}
                    setIsOwnerRowClicked={setIsOwnerRowClicked}
                    setExpandedValue={setExpandedValue}
                    isTaxIdNumbersLoading={
                      isOrganizationsLoading || organizationsQueryResult.loading
                    }
                    setIsTaxIdRowClicked={setIsTaxIdRowClicked}
                    setIsSaveDisabled={setIsSaveDisabled}
                    setCanCallProviders={setCanCallProviders}
                    isAccordionDisabled={isAccordionDisabled}
                    allowProviderSearch={allowProviderSearch}
                    setIsCareProviderAccordionDisabled={
                      setIsCareProviderAccordionDisabled
                    }
                  />
                </StyledAccordionItem>

                <StyledAccordionItem
                  isDisabled={
                    (!careProvidersError && isCareProviderAccordionDisabled) ||
                    isAccordionDisabled
                  }
                  allowProviderSearch={allowProviderSearch}
                  value="care-provider"
                >
                  <CareProvider
                    selectedProvider={selectedProvider}
                    isCareProviderAccordionExpanded={
                      isCareProviderAccordionExpanded
                    }
                    setSelectedProvider={setSelectedProvider}
                    careProviders={careProviders}
                    careProvidersError={careProvidersError}
                    isTaxIdRowClicked={isTaxIdRowClicked}
                    setIsTaxIdRowClicked={setIsTaxIdRowClicked}
                    setIsOwnerRowClicked={setIsOwnerRowClicked}
                    isCareProvidersLoading={
                      isCareProvidersLoading || careProvidersQueryResult.loading
                    }
                    expandedValue={expandedValue}
                    setExpandedValue={setExpandedValue}
                    setIsCareProviderAccordionExpanded={
                      setIsCareProviderAccordionExpanded
                    }
                    defaultProvider={defaultProvider}
                    setIsSaveDisabled={setIsSaveDisabled}
                    setIsCareProviderAccordionDisabled={
                      setIsCareProviderAccordionDisabled
                    }
                    isAccordionDisabled={isAccordionDisabled}
                  />
                </StyledAccordionItem>
              </Accordion>
            </Drawer.Section>
          </Otherwise>
        </Choose>
      </StyledProviderDrawer>

      <ProviderConfirmationModal
        isOpen={confirmProvider}
        onCancel={handleCancel}
        onContinue={handleSave}
      />
    </React.Fragment>
  );
};
